import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import LandingPage from "@/pages/LandingPage";
import InstallPage from "@/pages/InstallPage";
import Dashboard from "@/pages/Dashboard";
import PatientDetail from "@/pages/PatientDetail";
import SessionRecording from "@/pages/SessionRecording";
import TranscriptViewer from "@/pages/TranscriptViewer";
import Subscription from "@/pages/Subscription";
import ContactSalesPage from "@/pages/ContactSalesPage";
import PracticePage from "@/pages/PracticePage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import AdminPage from "@/pages/AdminPage";
import RevenueDashboard from "@/pages/RevenueDashboard";
import PrivacyPolicyPage from "@/pages/legal/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/legal/TermsOfServicePage";
import HipaaCompliancePage from "@/pages/legal/HipaaCompliancePage";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import OfflineStatusBanner from "@/components/OfflineStatusBanner";
import { Toaster } from "@/components/ui/sonner";
import { Clock, Zap } from "lucide-react";
import { clearCache, clearCacheForOtherUsers } from "@/lib/offlineCache";
import { cachedGet } from "@/lib/offlineApi";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

function TrialBanner({ token }) {
  const [subStatus, setSubStatus] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return;
    cachedGet(`${API}/subscription/status`, token, { ttlMs: 6 * 60 * 60 * 1000 })
      .then(({ data }) => setSubStatus(data))
      .catch(() => {});
  }, [token]);

  if (!subStatus || subStatus.status === 'subscribed') return null;

  if (subStatus.status === 'trial') {
    const daysLeft = subStatus.days_left ?? 0;
    const hoursLeft = subStatus.hours_left ?? 0;
    const isEndingSoon = daysLeft <= 3;
    // Urgent styling kicks in when the trial is ≤ 3 days away from expiring.
    if (isEndingSoon) {
      const timeLeftLabel = daysLeft <= 0
        ? `${hoursLeft} hour${hoursLeft !== 1 ? 's' : ''}`
        : `${daysLeft} day${daysLeft !== 1 ? 's' : ''}`;
      return (
        <div
          className="bg-gradient-to-r from-amber-500 to-red-600 text-white text-center py-2.5 px-3 text-xs sm:text-sm flex flex-wrap items-center justify-center gap-x-3 gap-y-1 shadow-md"
          data-testid="trial-ending-banner"
        >
          <span className="flex items-center gap-1.5 font-semibold">
            <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-pulse" />
            Trial ending in <strong className="font-extrabold">{timeLeftLabel}</strong> — subscribe now to keep your access
          </span>
          <button
            onClick={() => navigate('/subscription')}
            data-testid="trial-ending-subscribe-btn"
            className="bg-white text-red-600 hover:bg-red-50 px-4 py-1 rounded-full text-xs font-bold transition-colors whitespace-nowrap"
          >
            <Zap className="w-3 h-3 inline mr-1" />Subscribe — $79.99/mo
          </button>
        </div>
      );
    }
    return (
      <div className="bg-gradient-to-r from-purple-600 to-slate-600 text-white text-center py-2 px-3 text-xs sm:text-sm flex flex-wrap items-center justify-center gap-x-3 gap-y-1" data-testid="trial-banner">
        <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        Free trial — <strong>{daysLeft} day{daysLeft !== 1 ? 's' : ''}</strong> remaining</span>
        <button onClick={() => navigate('/subscription')} className="bg-white/20 hover:bg-white/30 px-3 py-0.5 rounded-full text-xs font-semibold transition-colors whitespace-nowrap">
          <Zap className="w-3 h-3 inline mr-1" />Subscribe
        </button>
      </div>
    );
  }

  if (subStatus.status === 'expired') {
    return (
      <div className="bg-red-600 text-white text-center py-2.5 px-3 text-xs sm:text-sm flex flex-wrap items-center justify-center gap-3" data-testid="expired-banner">
        <span>Your free trial has ended.</span>
        <button onClick={() => navigate('/subscription')} className="bg-white text-red-600 px-4 py-1 rounded-full text-xs font-bold hover:bg-red-50 transition-colors whitespace-nowrap">
          Subscribe — $79.99/mo
        </button>
      </div>
    );
  }

  return null;
}

function SubscriptionGate({ children, token }) {
  const [subStatus, setSubStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    cachedGet(`${API}/subscription/status`, token, { ttlMs: 6 * 60 * 60 * 1000 })
      .then(({ data }) => { setSubStatus(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  if (loading) return null;
  if (!subStatus) return children;
  if (subStatus.active) return children;

  // Trial expired — redirect to subscription. But: if we're offline AND
  // the cached status said active, the cachedGet path already returned
  // active. If we have NO cached status at all and are offline, let the
  // user in (don't lock them out of the app for a network blip).
  if (typeof navigator !== 'undefined' && !navigator.onLine) return children;
  return <Navigate to="/subscription" replace />;
}

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (token) {
      fetchUser();
    }
  }, [token]);

  // Register the service worker once (PWA install + offline shell)
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker
          .register('/service-worker.js', { scope: '/' })
          .catch((err) => console.warn('SW registration failed:', err));
      });
    }
  }, []);

  const fetchUser = async () => {
    try {
      const { data } = await cachedGet(`${API}/auth/me`, token, { ttlMs: 7 * 24 * 60 * 60 * 1000 });
      let me = data;

      // Auto-detect the user's IANA timezone from the browser and push it
      // to the backend if the stored value is still the app default. This
      // ensures notes & session timestamps render in the provider's own
      // timezone without requiring a manual settings change on day one.
      // Skip this side-effect when offline — it would just fail.
      if (typeof navigator === 'undefined' || navigator.onLine) {
        try {
          const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (browserTz && browserTz !== me.timezone && me.timezone === 'America/New_York') {
            const patched = await axios.patch(`${API}/auth/me`, { timezone: browserTz }, {
              headers: { Authorization: `Bearer ${token}` }
            }).catch(() => null);
            if (patched?.data) me = patched.data;
          }
        } catch {}
      }

      setUser(me);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Don't sign the user out for offline / network errors — they
      // would lose offline access to their own data.
      const isNetworky =
        !error?.response || error?.code === 'ERR_NETWORK' ||
        (typeof navigator !== 'undefined' && !navigator.onLine);
      if (!isNetworky) logout();
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    // Different user signing in on this device → drop the previous
    // user's cached PHI so it cannot leak across accounts.
    clearCacheForOtherUsers(newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    // Wipe cached PHI on sign-out (HIPAA hygiene).
    clearCache();
    setToken(null);
    setUser(null);
  };

  const PrivateRoute = ({ children, gated = true }) => {
    if (!token) return <Navigate to="/landing" />;
    if (gated) return <SubscriptionGate token={token}>{children}</SubscriptionGate>;
    return children;
  };

  return (
    <div className="App">
      {/* Hidden element for browser extension to read credentials */}
      {token && (
        <div
          id="plexura-extension-credentials"
          data-app-url={BACKEND_URL}
          data-auth-token={token}
          style={{ display: 'none' }}
        />
      )}
      <BrowserRouter>
        {token && <TrialBanner token={token} />}
        <Routes>
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/install" element={<InstallPage />} />
          <Route path="/contact" element={<ContactSalesPage />} />
          {/* Public legal pages — linked from the landing page footer */}
          <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/legal/terms" element={<TermsOfServicePage />} />
          <Route path="/legal/hipaa" element={<HipaaCompliancePage />} />
          <Route path="/accept-invite/:token" element={<AcceptInvitePage token={token} />} />
          <Route path="/login" element={<LoginPage onLogin={login} />} />
          <Route path="/register" element={<RegisterPage onLogin={login} />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage onLogin={login} />} />
          <Route
            path="/subscription"
            element={
              <PrivateRoute gated={false}>
                <Subscription user={user} token={token} onLogout={logout} />
              </PrivateRoute>
            }
          />
          <Route
            path="/practice"
            element={
              <PrivateRoute gated={false}>
                <PracticePage user={user} token={token} />
              </PrivateRoute>
            }
          />
          <Route
            path="/integrations"
            element={
              <PrivateRoute gated={false}>
                <IntegrationsPage token={token} />
              </PrivateRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <PrivateRoute gated={false}>
                <AnalyticsPage user={user} token={token} />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <PrivateRoute gated={false}>
                <AdminPage token={token} />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/revenue"
            element={
              <PrivateRoute gated={false}>
                <RevenueDashboard token={token} />
              </PrivateRoute>
            }
          />
          <Route
            path="/"
            element={
              <PrivateRoute>
                <Dashboard user={user} token={token} onLogout={logout} onUserUpdate={setUser} />
              </PrivateRoute>
            }
          />
          <Route
            path="/patient/:patientId"
            element={
              <PrivateRoute>
                <PatientDetail user={user} token={token} onLogout={logout} />
              </PrivateRoute>
            }
          />
          <Route
            path="/session/:sessionId/record"
            element={
              <PrivateRoute>
                <SessionRecording user={user} token={token} onLogout={logout} />
              </PrivateRoute>
            }
          />
          <Route
            path="/session/:sessionId/transcript"
            element={
              <PrivateRoute>
                <TranscriptViewer user={user} token={token} onLogout={logout} />
              </PrivateRoute>
            }
          />
        </Routes>
      </BrowserRouter>
      <PWAInstallPrompt />
      <OfflineStatusBanner />
      <Toaster />
    </div>
  );
}

export default App;
